<script lang="ts" setup>
  import { onUnmounted, computed } from 'vue';
  import { $redirect, route, useSession, useQuery } from '@procommerce/visitor';
  import { useTimer } from '$storefront';
  import { CartLayout } from '$components';

  defineOptions({
    layout: CartLayout,
    inheritAttrs: false,
  });

  defineProps({
    order: { type: Object, require: true },
    payment: { type: Object, require: true },
    method: { type: Object, require: true },
  });


  const session = useSession();
  const query = useQuery();

  const isAuthenticated = computed(() => {
    return session.value.is_authenticated;
  });

  const { formatted, stopTimer } = useTimer({
    timestamp: query.value.expires,
    callback: () => {
      $redirect(route('web.homepage.index'));
    },
  });

  onUnmounted(() => {
    stopTimer();
  });
</script>
<template>
  <div class="order">
    <div class="order-failure">
      <SfTile class="order-failure__confirmation">
        <div class="order-failure__icon-wrapper">
          <SfIconDanger />
        </div>
        <main class="order-failure__content">
          <article class="order-failure__header">
            <header class="order-failure__heading-wrapper">
              <h1 class="order-failure__heading">{{ $t('Płatność za zamówienie nie powiodła&nbsp;się!') }}</h1>
            </header>
            <section class="order-failure__message-wrapper">
              <p class="order-failure__message">{{ $t('Na twój adres e-mail wysłaliśmy potwierdzenie zamówienia, wraz z linkiem do szczegółów zamówienia.') }}</p>
              <p v-if="isAuthenticated" class="order-failure__account-info">{{ $t('Zamówienie znajdziesz też w zakładce Moje Konto.') }}</p>
            </section>
          </article>

          <article class="order-failure__repay">
            <section class="order-failure__repay-section">
              <p class="order-failure__repay-text">{{ $t('Przechodząc do szczegółów zamówienia możesz spróbować ponownie opłacić swoje zamówienie.') }}</p>
            </section>
          </article>

          <article class="order-failure__order">
            <section class="order-failure__order-section">
              <p class="order-failure__order-id">{{ $t('Numer twojego zamówienia') + ': ' }}<strong>{{ order.id }}</strong></p>
            </section>
          </article>

          <nav class="order-failure__navigation">
            <SfButton :href="route('web.homepage.index')" large>
              {{ $t('Strona główna') }}
            </SfButton>
          </nav>

          <article class="order-failure__security">
            <p class="order-failure__security-text">{{ $t('Ze względów bezpieczeństwa ta&nbsp;strona wygasa&nbsp;za') + ': ' }}<b>{{ formatted }}</b></p>
          </article>
        </main>
      </SfTile>
    </div>
  </div>
</template>
<style lang="scss">
  @use "$assets/mixins/media";

  .icon-danger-marker {
    fill: var(--sf-order-failure-icon-color-marker);
    stroke: var(--sf-order-failure-icon-color-marker);
  }

  .icon-danger-border {
    fill: var(--sf-order-failure-icon-color);
    stroke: var(--sf-order-failure-icon-color);
  }

  .order-failure {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 2rem 0;

    &__confirmation {
      position: relative;
      width: 100%;
      max-width: 700px;
      margin-top: 3rem;
    }

    &__icon-wrapper {
      position: absolute;
      top: 0;
      left: 50%;
      display: flex;
      justify-content: center;
      align-items: center;
      transform: translate(-50%, -50%);
      height: 100px;
      width: 100px;
      font-size: 3rem;
      color: var(--sf-order-failure-icon-color);
      border-radius: 50%;
    }

    &__content,
    &__header {
      display: flex;
      flex-direction: column;
    }

    &__content {
      gap: 1.5rem;
    }

    &__header {
      gap: 1rem;
    }

    &__heading {
      text-align: center;
      margin: 3rem 0 2rem;
    }

    &__order {
      font-size: 1.25rem;
    }

    &__navigation {
      display: flex;
      justify-content: center;
      width: 100%;
      margin: 1rem 0;
    }

    &__security {
      font-size: 0.75rem;
    }
  }

  @include media.query(lg) {
    .order-failure {
      margin: 0;
      padding: 1rem;

      &__icon-wrapper {
        width: 70px;
        height: 70px;

        svg {
          font-size: 2rem;
        }
      }

      &__confirmation {
        border-radius: var(--sf-tile-border-radius, 0);
        box-shadow: var(--sf-tile-interactive-box-shadow);
        padding: 1rem 0.5rem;
      }

      &__header {
        text-align: center;
        margin-top: 1rem;
      }

      &__order {
        text-align: center;
      }

      &__heading {
        font-size: 1.5rem;
        margin: 1rem 0;
      }
    }

    @include media.query(md) {
      .order-failure__security, .order-failure__repay-text {
        text-align: center;
      }
    }
  }
</style>
